import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type FileMessage, type UserMessage } from "@sendbird/chat/message";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import { type CoreMessageType, type SendableMessageType } from "@sendbird/uikit-react/types/utils";
import FileMessageItemBody from "@sendbird/uikit-react/ui/FileMessageItemBody";
import MessageContent from "@sendbird/uikit-react/ui/MessageContent";
import ThumbnailMessageItemBody from "@sendbird/uikit-react/ui/ThumbnailMessageItemBody";
import { useCallback, useState } from "react";

import { useSetEditingMessage, useSetQuoteMessage } from "../context/EditMessageContext";
import { useLongPress } from "../hooks/useLongPress";
import { UserCustomChatMessage } from "../UserCustomChatMessage";
import { MessageActionButtons } from "./MessageActionButtons";

interface MessageContentWithLongPressProps {
  channel: GroupChannel;
  chainBottom?: boolean;
  chainTop?: boolean;
  message: CoreMessageType;
  renderMessageHeader?: (props: { message: CoreMessageType }) => React.ReactNode;
  userId: string;
}

function isUserMessage(message: CoreMessageType): message is UserMessage {
  return message.messageType === "user";
}

export function MessageContentWithLongPress(props: MessageContentWithLongPressProps) {
  const { channel, chainBottom, chainTop, message, renderMessageHeader, userId } = props;

  /* oxlint-disable typescript/unbound-method -- Sendbird context methods are stable references */
  const {
    deleteMessage,
    resendMessage,
    setQuoteMessage: setSendbirdQuoteMessage,
  } = useChannelContext();
  /* oxlint-enable typescript/unbound-method */
  const setEditingMessage = useSetEditingMessage();
  const setQuoteMessage = useSetQuoteMessage();
  const [showActions, setShowActions] = useState(false);

  const isMessageByCurrentUser = (message as SendableMessageType).sender?.userId === userId;

  const handleLongPress = useCallback(() => {
    if (isUserMessage(message)) {
      setShowActions(true);
    }
  }, [message]);

  const handleCloseActions = useCallback(() => {
    setShowActions(false);
  }, []);

  const handleReply = useCallback(() => {
    if (isUserMessage(message)) {
      setQuoteMessage(message);
      setSendbirdQuoteMessage(message);
    }
  }, [message, setQuoteMessage, setSendbirdQuoteMessage]);

  const handleCopy = useCallback(() => {
    // Fallback - handled in MessageActionButtons
  }, []);

  const handleDelete = useCallback(async () => {
    if (isUserMessage(message)) {
      await deleteMessage(message);
    }
  }, [message, deleteMessage]);

  const handleResend = useCallback(() => {
    if (isUserMessage(message)) {
      resendMessage(message);
    }
  }, [message, resendMessage]);

  const handleEdit = useCallback(() => {
    if (isUserMessage(message)) {
      setEditingMessage(message);
    }
  }, [message, setEditingMessage]);

  const longPressHandlers = useLongPress({
    onLongPress: handleLongPress,
  });

  const renderMessageBody = useCallback(
    (bodyProps: { message: CoreMessageType; isByMe: boolean }) => {
      const { message: bodyMessage, isByMe: bodyIsByMe } = bodyProps;

      if (bodyMessage.messageType === "file") {
        const fileMessage = bodyMessage as FileMessage;
        const isImageOrVideo =
          fileMessage.type?.startsWith("image/") || fileMessage.type?.startsWith("video/");

        if (isImageOrVideo) {
          return <ThumbnailMessageItemBody isByMe={bodyIsByMe} message={fileMessage} />;
        }

        return <FileMessageItemBody isByMe={bodyIsByMe} message={fileMessage} />;
      }

      if (bodyMessage.messageType === "user") {
        return (
          <UserCustomChatMessage
            channel={channel}
            isMessageByCurrentUser={bodyIsByMe}
            message={bodyMessage as UserMessage}
          />
        );
      }

      return null;
    },
    [channel]
  );

  return (
    <Box sx={{ position: "relative" }}>
      {isUserMessage(message) && (
        <MessageActionButtons
          isMessageByCurrentUser={isMessageByCurrentUser}
          isReply={isDefined(message.parentMessage)}
          isVisible={showActions}
          message={message}
          onClose={handleCloseActions}
          onCopy={handleCopy}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onReply={handleReply}
          onResend={handleResend}
        />
      )}

      <Box
        {...longPressHandlers}
        sx={(theme) => ({
          cursor: "pointer",
          userSelect: "none",
          position: "relative",
          zIndex: showActions ? theme.zIndex.tooltip : "auto",
          transform: showActions ? "scale(1.08)" : "scale(1)",
          transformOrigin: isMessageByCurrentUser ? "right center" : "left center",
          transition: "transform 0.2s ease-out",
          width:
            isUserMessage(message) || message.messageType === "file" ? "fit-content" : undefined,
          marginLeft: isMessageByCurrentUser ? "auto" : undefined,
          "& .sendbird-message-content__left.outgoing & .sendbird-message-content__right.incoming":
            {
              pointerEvents: "none",
            },
        })}
      >
        <MessageContent
          chainTop={chainTop}
          chainBottom={chainBottom}
          channel={channel}
          message={message}
          renderMessageBody={renderMessageBody}
          renderMessageHeader={renderMessageHeader}
          renderMessageMenu={() => null}
          renderSenderProfile={() => null}
          userId={userId}
        />
      </Box>
    </Box>
  );
}
